// Pure `showIf` gate evaluator — the SINGLE source of truth for conditional-field
// visibility, shared by the client render paths (card/table via GenericCategoryPage,
// the expandable drawer via ExpandableSectionLayout) AND the server (GATE-CLEAR-1,
// which must clear a gated field's value when its gate closes — the same predicate,
// server-side). Lifted out of client/src/lib/evaluate-show-if.ts (which now re-exports
// this) so there is exactly ONE evaluator; a drift between client-hide and server-clear
// would re-open the hidden-uncorrectable-value bug this system exists to close.
//
// A condition's `fieldId` always targets a SIBLING field in the same section (the
// reconciler resolves the authoring `fieldKey` → DB id within-section), so the caller
// supplies a map of that section's fieldId → current entry value. Logic is byte-for-byte
// the original GenericCategoryPage implementation.

export type ShowIfOperator =
  | "equals"
  | "not_equals"
  | "contains"
  | "is_empty"
  | "is_not_empty"
  | "in"
  | "not_in";

/** The resolved condition shape stored on `generic_fields.conditions` and read by both
 *  client and server (always keyed by DB `fieldId`, never `fieldKey`). */
export type ShowIfCondition = {
  showIf: { fieldId: string; operator: string; value: string };
  /** s234 (D-234.37): alternatives, OR'd with `showIf` — the field shows when ANY one holds. */
  anyOf?: { fieldId: string; operator: string; value: string }[];
};

/** Minimal shape the evaluator reads. Both `EntryMap` (a `GenericEntry` map) and a
 *  lightweight `{ value }` per-row map satisfy it. Keyed by the sibling field's id. */
export type ShowIfEntryMap = Record<string, { value?: string | null } | undefined>;

/** True when the field is VISIBLE (gate open). False → gate closed → GATE-CLEAR-1 blanks it. */
export function evaluateShowIf(
  conditions: ShowIfCondition | null | undefined,
  entryMap: ShowIfEntryMap,
): boolean {
  if (!conditions?.showIf?.fieldId) return true;
  if (holds(conditions.showIf, entryMap)) return true;
  return (conditions.anyOf ?? []).some((c) => !!c?.fieldId && holds(c, entryMap));
}

/** One test of one sibling's value — `showIf` or one of its `anyOf` alternatives. */
function holds(test: { fieldId: string; operator: string; value: string }, entryMap: ShowIfEntryMap): boolean {
  const { fieldId, operator, value } = test;
  const watchValue = (entryMap[fieldId]?.value ?? "").trim();
  switch (operator) {
    case "is_empty":     return watchValue === "";
    case "is_not_empty": return watchValue !== "";
    case "equals":       return watchValue.toLowerCase() === value.toLowerCase();
    case "not_equals":   return watchValue.toLowerCase() !== value.toLowerCase();
    case "contains":     return watchValue.toLowerCase().includes(value.toLowerCase());
    // Multi-value membership (D-154.3): `value` is a PIPE-delimited list ("A|B|C").
    // Gate opens when the watched value equals ANY listed option (case-insensitive,
    // trimmed). Pipe chosen as the delimiter because option labels never contain it
    // (they DO contain commas — e.g. "Joint – … (passes to co-owner, outside your estate)").
    case "in":           return inList(value, watchValue);
    // 🔴 EXCLUSION gate (s156 G-follow-up, ruled): the field is visible UNLESS the watched value is
    // one of the listed options. Same pipe-delimited list as `in`, deliberately NOT its plain
    // negation in spirit: an UNSET watched value opens this gate. That is the whole point — an
    // `equals`-style gate hides the field until the planner answers the gating question, which hid
    // beneficiary first names on every row whose `beneficiary_type` was never set. Excluding by
    // exception keeps a field visible by default and hides it only on an explicit, known exclusion.
    case "not_in":       return !inList(value, watchValue);
    default:             return true;
  }
}

/** Pipe-delimited membership, case-insensitive + trimmed. Shared by `in` / `not_in` so the two can
 *  never disagree about what "listed" means. Pipe is the delimiter because option labels never
 *  contain one (they DO contain commas). */
function inList(value: string, watchValue: string): boolean {
  return value.split("|").map((v) => v.trim().toLowerCase()).includes(watchValue.toLowerCase());
}

/** The sibling fieldId a condition gates on, or null if the field is ungated. */
export function gateSourceFieldId(conditions: ShowIfCondition | null | undefined): string | null {
  return conditions?.showIf?.fieldId ?? null;
}

/** s234 (D-234.37): every sibling fieldId a condition reads — `showIf`'s, then each `anyOf` alternative's. */
export function gateSourceFieldIds(conditions: ShowIfCondition | null | undefined): string[] {
  if (!conditions?.showIf?.fieldId) return [];
  return Array.from(new Set([conditions.showIf.fieldId, ...(conditions.anyOf ?? []).map((c) => c?.fieldId).filter((x): x is string => !!x)]));
}
